import axios from 'axios';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import Alert from '@mui/material/Alert';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import CheckIcon from '@mui/icons-material/CheckCircle';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import UploadIcon from '@mui/icons-material/Upload';

import {
  useCreateFileMutation,
  useUpdateFileMutation
} from '../../redux/services/fileApi';
import CustomHelperText from '../common/CustomHelperText';
import CustomInputLabel from '../common/CustomInputLabel';
import CustomTextField from '../common/CustomTextField';
import './FileUploadInput.scss'
import { VIEW_PATIENT_SCREEN_NAME } from "../../config/constants";

const fileExtensionMap = {
  'image/png': 'png',
  'image/jpeg': 'jpeg',
  'image/gif': 'gif',
  'application/pdf': 'pdf',
  'application/docx': 'docx',
  'application/xlsx': 'xlsx',
  'application/odt': 'odt',
  'application/ods': 'ods',
  'video/mov': 'mov',
  'video/mpeg': 'mpeg',
  'video/mp4': 'mp4',
  'video/wmv': 'wmv',
};

const handleUpload = ({
  createPresignedUrl,
  updateFile,
  setError,
  setSuccess,
  setIsUploading,
  fileType,
  mimeType,
  patientId = null,
  title = null,
  description = null,
}) => async ({ file }) => {
  // Set state
  setSuccess(false);
  setError(null);
  setIsUploading(true);
  // handle case if file is missing
  if (!file) {
    setIsUploading(false);
    return setError(new Error('No file was attached.'));
  }
  const fileName = (title || file.name).replace(/\.[3,4][a-z]$/i, '');
  try {
    let params = {
      file_type: fileType,
      mime_type: file.type,
      file_name: fileName,
      file_extension: get(fileExtensionMap, file.type) || null,
    };
    if (fileType === 'patient_file') params.patient_id = patientId;
    if (description) params.description = description;
    const fileResponse = await createPresignedUrl(params);
    if (fileResponse.error) {
      setSuccess(false);
      setIsUploading(false);
      return setError(new Error(
        get(fileResponse, 'error.data.errors[0]')
        || 'A networking error occurred. Try refreshing the page in your browser.'
      ));
    }
    const url = get(fileResponse, 'data.url');
    const response = await axios.put(
      url,
      file,
      { headers: { 'Content-Type': file.type } }
    );
    const updateResponse = updateFile({
      id: fileResponse.data.id,
      body: { uploaded: true }
    });
    if (updateResponse.error) {
      setSuccess(false);
      setIsUploading(false);
      return setError(new Error('Failed to upload file.'));
    }
    setSuccess(true);
    setIsUploading(false);
    setError(null);
  } catch (err) {
    setError(err);
    setIsUploading(false);
    setSuccess(false);
  }
};

const FileUploadInput = props => {
  const [title, setTitle] = useState(null);
  const [description, setDescription] = useState(null);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [createPresignedUrl, response] = useCreateFileMutation();
  const [updateFile] = useUpdateFileMutation();
  const isLoading = response.isLoading || isUploading;
  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
    maxFiles: props.maxFiles,
    accept: props.accept,
    // disabled: !url || response.isLoading,
  });
  const file = get(acceptedFiles, '[0]');
  const onUpload = handleUpload({
    title,
    description,
    fileType: props.fileType,
    mimeType: props.mimeType,
    patientId: props.patientId,
    createPresignedUrl,
    updateFile,
    setError,
    setSuccess,
    setIsUploading,
  });
  const fileName = get(file, 'name') || null;
  useEffect(() => {
    // if the user drops a new file, reset the state
    if (fileName) {
      setSuccess(false);
      setError(false);
      setIsUploading(false);
    }
  }, [fileName]);
  return (
    <Stack className='FileUploadInput' spacing={1}>
      {
        props.screen === VIEW_PATIENT_SCREEN_NAME
          ?
          <>
            <Stack direction={{ sm: 'column', md: 'row' }} spacing={2} padding={1}>
              <Stack width={{ sm: '100%', md: '50%' }}>
                {
                  props.label
                    ? <CustomInputLabel>
                      {props.label}
                    </CustomInputLabel>
                    : null
                }
                <Stack direction='row' spacing={1}>
                  {
                    file
                      ? <img
                        src={URL.createObjectURL(file)}
                        className='FileUploadInput__img-container'
                        alt=""
                      />
                      : (
                        props.src
                          ? <img
                            src={props.src}
                            className='FileUploadInput__img-container'
                            alt=""
                          />
                          : null
                      )
                  }
                  <section className="container">
                    <div
                      {...getRootProps({ className: 'upload-dropzone dropzone' })}
                    >
                      {
                        !success && !isLoading
                          ? <Stack justfiyContent='center' alignItems='center' padding={2}>
                            <input {...getInputProps()} />
                            <Stack direction='row' alignItems='center' spacing={0.5}>
                              <AttachFileIcon color='primary' />
                              <Typography variant='paragraphMediumInter' color='text.light' align='center'>
                                {
                                  file
                                    ? file.name
                                    : 'Click to browse files.'
                                }
                              </Typography>
                            </Stack>
                          </Stack>
                          : null
                      }
                      {
                        success
                          ? <Stack justifyContent='center' alignItems='center'>
                            <CheckIcon style={{ fontSize: '2em' }} color='success' />
                            <Typography variant='body1' color='grey'>
                              Your file was saved!
                            </Typography>
                          </Stack>
                          : null
                      }
                      {
                        isLoading
                          ? <Stack justifyContent='center' alignItems='center' padding={2}>
                            <CircularProgress />
                          </Stack>
                          : null
                      }
                      {
                        error || response.error
                          ? <Alert severity='error'>
                            {
                              get(error, 'message')
                              || get(response, 'error.data.errors[0]')
                              || 'Error uploading file'
                            }
                          </Alert>
                          : null
                      }
                    </div>
                  </section>
                </Stack>
                <CustomHelperText>
                  {'Allowed file types: '}
                  {Object.values(props.accept).flatMap(str => str).join(', ')}
                </CustomHelperText>
                {
                  props.helperText
                    ? <CustomHelperText>
                      {props.helperText}
                    </CustomHelperText>
                    : null
                }
              </Stack>
              <Stack width={{ sm: '100%', md: '50%' }}>
                {
                  props.allowTitle
                    ? <CustomTextField
                      label='Title'
                      placeholder={props?.titlePlaceholder || null}
                      helperText={props?.titleHelperText || null}
                      onChange={e => setTitle(e.target.value)}
                    />
                    : null
                }
                {
                  props.allowDescription
                    ? <CustomTextField
                      label='Description'
                      onChange={e => setDescription(e.target.value)}
                    />
                    : null
                }
              </Stack>
            </Stack>
            <Stack className='FileUploadInput__button-container'>
              <Button
                className='FileUploadInput__upload-btn'
                variant='contained'
                disabled={isUploading}
                onClick={() => onUpload({ file })}
              >
                <UploadIcon />
                Submit File
              </Button>
            </Stack>
          </>
          :
          <>
            {
              props.label
                ? <CustomInputLabel>
                  {props.label}
                </CustomInputLabel>
                : null
            }
            <Box className='FileUploadInput__wrapper'>
              <Box className='FileUploadInput__avatar-box'>
                {
                  file
                    ? <img
                      src={URL.createObjectURL(file)}
                      className='FileUploadInput__img-container'
                      alt=""
                    />
                    : (
                      props.src
                        ? <img
                          src={props.src}
                          className='FileUploadInput__img-container'
                          alt=""
                        />
                        : null
                    )
                }
              </Box>
              <Box className='FileUploadInput__right'>
                <Box className='FileUploadInput__dropzone-box'>
                  <div
                    {...getRootProps({ className: 'upload-dropzone dropzone' })}
                  >
                    {
                      !success && !isLoading
                        ? <Stack justfiyContent='center' alignItems='center' padding={2}>
                          <input {...getInputProps()} />
                          <Stack direction='row' alignItems='center' spacing={0.5}>
                            <AttachFileIcon color='primary' />
                            <Typography variant='paragraphMediumInter' color='text.light' align='center'>
                              {
                                file
                                  ? file.name
                                  : 'Click to browse files.'
                              }
                            </Typography>
                          </Stack>
                        </Stack>
                        : null
                    }
                    {
                      success
                        ? <Stack justifyContent='center' alignItems='center'>
                          <CheckIcon style={{ fontSize: '2em' }} color='success' />
                          <Typography variant='body1' color='grey'>
                            Your file was saved!
                          </Typography>
                        </Stack>
                        : null
                    }
                    {
                      isLoading
                        ? <Stack justifyContent='center' alignItems='center' padding={2}>
                          <CircularProgress />
                        </Stack>
                        : null
                    }
                    {
                      error || response.error
                        ? <Alert severity='error'>
                          {
                            get(error, 'message')
                            || get(response, 'error.data.errors[0]')
                            || 'Error uploading file'
                          }
                        </Alert>
                        : null
                    }
                  </div>
                </Box>
                <Box className='FileUploadInput__info-box'>
                  <Box className='FileUploadInput__info-box__texts'>
                  <Typography
                    variant='extraLarge'
                    lineHeight='normal'
                    color='text.light'
                    component='p'
                    style={{
                      paddingTop: '0.25em',
                      paddingRight: '0.25em',
                      paddingLeft: '0.25em'
                    }}
                  >
                      {'Allowed file types: '}
                      {Object.values(props.accept).flatMap(str => str).join(', ')}
                      {'.'}
                    </Typography>
                    {
                      props.helperText
                        ? <Typography
                            variant='extraLarge'
                            lineHeight='normal'
                            color='text.light'
                            component='p'
                            style={{
                              paddingTop: '0.25em',
                              paddingRight: '0.25em',
                              paddingLeft: '0.25em'
                            }}
                          >
                          {props.helperText}
                        </Typography>
                        : null
                    }
                    {
                      props.allowTitle
                        ? <CustomTextField
                          label='Title'
                          placeholder={props?.titlePlaceholder || null}
                          helperText={props?.titleHelperText || null}
                          onChange={e => setTitle(e.target.value)}
                        />
                        : null
                    }
                    {
                      props.allowDescription
                        ? <CustomTextField
                          label='Description'
                          onChange={e => setDescription(e.target.value)}
                        />
                        : null
                    }
                  </Box>
                  <Box className='FileUploadInput__info-box__buttons'>
                    <Button
                      variant='primaryGradient'
                      disabled={isUploading}
                      onClick={() => onUpload({ file })}
                      endIcon={<UploadIcon />}
                    >
                      Submit File
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>


          </>
      }
    </Stack>
  );
};

FileUploadInput.defaultProps = {
  maxFiles: 1,
  // mimeType: 'image/png',
  accept: {
    'image/*': ['.png', '.jpeg', '.gif'],
    'application/*': ['.pdf', '.docx', '.xlsx', '.odt', '.ods', '.PDF'],
    'application/pdf': ['.pdf'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'video/*': ['.mov', '.mpeg', '.mp4', '.wmv'],
  },
};

FileUploadInput.propTypes = {
  fileType: PropTypes.string.isRequired,
  src: PropTypes.string,
  patientId: PropTypes.number,
  maxFiles: PropTypes.number,
  accept: PropTypes.object,
  label: PropTypes.string,
  helperText: PropTypes.string,
  allowTitle: PropTypes.bool,
  allowDescription: PropTypes.bool,
};

export default FileUploadInput;
